import { useCallback, useState } from 'react';
import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';

interface UseImageUploadResult {
  upload: (file: File) => Promise<string>;
  isUploading: boolean;
}

/**
 * Uploads an image to S3 via a presigned POST and returns the resulting asset id. The asset is
 * created public (`isPublic: true`) so it can be served from the `/d/public/{assetId}` endpoint.
 * Domain-agnostic — promoted from `features/templates-config` so any feature (e.g. the email widget
 * image-edit dialog) can reuse it.
 */
export function useImageUpload(): UseImageUploadResult {
  const [isUploading, setIsUploading] = useState(false);

  const upload = useCallback(async (file: File): Promise<string> => {
    setIsUploading(true);
    try {
      const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name, {
        isPublic: true,
      });

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      await uploadFileToS3(url, formData);
      return assetId;
    } finally {
      setIsUploading(false);
    }
  }, []);

  return { upload, isUploading };
}
